import {useApplicationData} from '@/Core/hooks/useApplicationData';
import {Button, Grid, Link, Tooltip, Box} from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import {toJS} from 'mobx';
import {observer} from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import {FC, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import Utils from '../../Core/Utils';
import {ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
import {QuoteData, QuoteHistory} from '../../Core/mobx/ParameterStore/types';
import RootStore from '../../Core/mobx/RootStore';
import {TemplatePurposes} from '../ContactForm/EmailEditor/EmailEditor';
import {MIN_COVERAGE_AMOUNT, MIN_PREMIUM_AMOUNT, validateHouseholdIncome, validateIllustration} from './constants';
import {copyToPreapproval} from './utils/copyToPreapproval';
import {shouldShowHHIWarning, WarningMessage} from '../Common/WarningMessage';
import AgeniumReviewDialog from './AgeniumReviewDialog';
import {AB_FLAGS} from '../../Core/constants/abFlags';

const Plot = dynamic(import('react-plotly.js'), {ssr: false});

interface QuotesTableDataProps {
    out: QuoteData[] | undefined;
    age: number;
    email: string;
    parameterStore: ParameterStore;
    data: QuoteHistory | null;
    formType?: 'single-quote' | 'combination-quote' | undefined;
    showEmailEditor: (p: TemplatePurposes) => void;
    handleModal?: boolean;
}

export const QuotesTableData: FC<QuotesTableDataProps> = observer(
    ({out, age, email, parameterStore, data, showEmailEditor}) => {
        const location = useLocation();
        const history = useHistory();

        const {applicationData, refetch} = useApplicationData();
        const contactsFormStore = RootStore.agentPortalStore.contactsFormStore;
        const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);

        const rawHHI = parameterStore.prequal.incomeHouseholdText.value ?? '';
        const hhi = Number(String(rawHHI).replace(/[,$]/g, '')) || 0;
        const {isValid, requiredMinIncome} = validateHouseholdIncome(age, hhi);
        const tooLowHHI = !isValid;

        const openModal = () => {
            parameterStore.updateProsperStatus('OPEN_AP');
            contactsFormStore.saveForm();
            const searchParams = new URLSearchParams(location.search);
            let currentPage =
                applicationData?.prosperActiveModalPage || parameterStore.prosperActiveModalPage || 'START_APPLICATION';
            refetch()
                .then((fetchedData) => {
                    currentPage = fetchedData?.prosperActiveModalPage || currentPage;
                })
                .finally(() => {
                    searchParams.set('modalPage', currentPage);
                    searchParams.set('quoteId', data?.id?.toString() || '');
                    history.push({
                        search: searchParams.toString(),
                        hash: location.hash,
                    });
                });
        };

        const carrier = toJS(out?.find((p) => p.name === 'Carrier'));
        const product = toJS(out?.find((p) => p.name === 'Product'));
        const isTAFE = product?.value === 'TA FE Express';
        const isADB = product?.value === 'ADB';
        const disableQuoteActions = isTAFE || isADB;
        const displayOut = out?.slice();
        const ageniumCaseId = applicationData?.ageniumCaseId || parameterStore.applicationId;
        const isReviewDisabled = !ageniumCaseId;

        // this is used for Prosper only for now
        let quoteValid = displayOut?.find((p) => p.name === 'Valid')?.value == 'valid';

        if (data?.singleQuote && data?.singleQuote.lapseAge && data?.singleQuote.quoteAge) {
            quoteValid = validateIllustration(
                data.singleQuote.monthlyPremium,
                data.singleQuote.faceAmount,
                data.singleQuote.lapseAge,
                data.singleQuote.quoteAge,
            );
        }

        // Continue to display if illustration is valid in quote data
        if (displayOut) {
            const validIndex = displayOut.findIndex((p) => p.name === 'Valid');
            const newValidEntry = {
                name: 'Valid',
                value: quoteValid ? 'valid' : 'invalid',
                type: 'string',
            };

            if (validIndex !== -1) {
                displayOut[validIndex] = newValidEntry;
            } else {
                displayOut.push(newValidEntry);
            }
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const format = (i: any) => {
            if (i.type == 'currency' && Utils.IsNumeric(i.value)) {
                let value: number = i.value;
                if (typeof i.value === 'string') {
                    value = parseFloat(i.value);
                }
                return value.toLocaleString('en-US', {style: 'currency', currency: 'USD'});
            }
            return i.value;
        };

        const formatCurrency = (n: number) => n.toLocaleString('en-US', {style: 'currency', currency: 'USD'});

        return (
            <Grid item>
                {displayOut?.length ? (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Label</TableCell>
                                    <TableCell>Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayOut
                                    ?.filter((o: any) => !Array.isArray(o.value))
                                    .map((o: any, i: any) => {
                                        const tableCellAriaLabel = `quote-table-value-for-${o.name.replace(/\s/g, '')}`;
                                        let showPremiumWarning = false;
                                        let premiumThreshold = 0;
                                        if (o.name === 'Initial monthly premium') {
                                            const initialMonthlyPremium = Number(o.value) || 0;
                                            // Get the AB flag value from the parameter store
                                            const lowerHouseholdIncomeLimit =
                                                RootStore.agentPortalStore.parameterStore.abFlags[
                                                    AB_FLAGS.LOWER_HOUSEHOLD_INCOME_LIMIT
                                                ] === true;
                                            const result = shouldShowHHIWarning({
                                                premium: initialMonthlyPremium,
                                                annualHHI: hhi,
                                                premiumMode: 'Monthly',
                                                lowerHouseholdIncomeLimit,
                                            });
                                            showPremiumWarning = result.exceeds;
                                            premiumThreshold = result.threshold;
                                        }
                                        return (
                                            <TableRow key={'K' + i}>
                                                <TableCell style={{maxWidth: 30}}>{o.name}</TableCell>
                                                <TableCell
                                                    style={{
                                                        maxWidth: 100,
                                                        wordWrap: 'break-word',
                                                    }}
                                                    aria-label={tableCellAriaLabel}
                                                >
                                                    {typeof o.value === 'string' && o.value.startsWith('https://') ? (
                                                        <Link underline="always" href={o.value} target="_blank">
                                                            Link
                                                        </Link>
                                                    ) : o.name === 'Initial monthly premium' ? (
                                                        <Box
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            {format(o)}
                                                            {showPremiumWarning && (
                                                                <WarningMessage value={premiumThreshold} tableCase />
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        format(o)
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                {email && (
                                    <TableRow>
                                        <TableCell colSpan={2}>
                                            <div style={{display: 'flex', justifyContent: 'center'}}>
                                                <Button
                                                    color="primary"
                                                    onClick={() => showEmailEditor(TemplatePurposes.quoted)}
                                                    variant="contained"
                                                    disabled={disableQuoteActions}
                                                >
                                                    Send quote email
                                                </Button>
                                                <Button
                                                    style={{marginLeft: 10}}
                                                    color="primary"
                                                    onClick={() => showEmailEditor(TemplatePurposes.afterSale)}
                                                    variant="contained"
                                                    disabled={disableQuoteActions}
                                                >
                                                    Send after sale email
                                                </Button>
                                                {carrier?.value !== 'EMC' && (
                                                    <Button
                                                        style={{marginLeft: 10}}
                                                        color="primary"
                                                        onClick={() => copyToPreapproval(parameterStore, data)}
                                                        variant="contained"
                                                        disabled={disableQuoteActions}
                                                    >
                                                        Copy to Pre-Approvals
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                            {carrier?.value === 'EMC' && (
                                <>
                                    <TableBody>
                                        <TableCell colSpan={2}>
                                            <div style={{display: 'flex', justifyContent: 'center', gap: 12}}>
                                                <Tooltip
                                                    title={
                                                        !quoteValid || tooLowHHI ? (
                                                            <div>
                                                                {!quoteValid && (
                                                                    <div>
                                                                        Eligibility issues:
                                                                        <div>
                                                                            • Monthly premium must be at least{' '}
                                                                            {formatCurrency(MIN_PREMIUM_AMOUNT)}.
                                                                        </div>
                                                                        <div>
                                                                            • Initial death benefit must be at least{' '}
                                                                            {formatCurrency(MIN_COVERAGE_AMOUNT)}.
                                                                        </div>
                                                                        <div>
                                                                            • Policy must not lapse at or before year 15
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {tooLowHHI && (
                                                                    <div
                                                                        style={{display: 'flex', alignItems: 'center'}}
                                                                    >
                                                                        <InfoOutlinedIcon style={{marginRight: 8}} />
                                                                        <div>
                                                                            Household income is below the minimum for
                                                                            age {age}.
                                                                            <div style={{fontWeight: 'normal'}}>
                                                                                Minimum required:{' '}
                                                                                {formatCurrency(requiredMinIncome)}.
                                                                                Entered: {formatCurrency(hhi)}.
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            ''
                                                        )
                                                    }
                                                    arrow
                                                    disableHoverListener={!(!quoteValid || tooLowHHI)}
                                                >
                                                    <span>
                                                        <Button
                                                            color="primary"
                                                            onClick={openModal}
                                                            variant="contained"
                                                            disabled={!quoteValid || tooLowHHI}
                                                        >
                                                            Launch instant app
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip
                                                    title={
                                                        isReviewDisabled
                                                            ? 'Instant app answers are available after the application is started.'
                                                            : ''
                                                    }
                                                    arrow
                                                    disableHoverListener={!isReviewDisabled}
                                                >
                                                    <span>
                                                        <Button
                                                            color="primary"
                                                            variant="contained"
                                                            onClick={() => setIsReviewDialogOpen(true)}
                                                            disabled={isReviewDisabled}
                                                            style={{textTransform: 'none', color: '#fff'}}
                                                        >
                                                            REVIEW INSTANT APP ANSWERS
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableBody>
                                    {!quoteValid && (
                                        <TableBody>
                                            <TableCell
                                                style={{backgroundColor: '#fdecea', fontWeight: 'bold'}}
                                                colSpan={2}
                                            >
                                                <div style={{display: 'flex', alignItems: 'center'}}>
                                                    <InfoOutlinedIcon style={{marginRight: 8}} />
                                                    <div>
                                                        Eligibility issues:
                                                        <div style={{fontWeight: 'normal'}}>
                                                            • Monthly premium must be at least{' '}
                                                            {formatCurrency(MIN_PREMIUM_AMOUNT)}.
                                                            <div>
                                                                • Initial death benefit must be at least{' '}
                                                                {formatCurrency(MIN_COVERAGE_AMOUNT)}.
                                                            </div>
                                                            <div>• Policy must not lapse at or before year 15</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                        </TableBody>
                                    )}
                                    {tooLowHHI && (
                                        <TableBody>
                                            <TableCell
                                                style={{backgroundColor: '#fdecea', fontWeight: 'bold'}}
                                                colSpan={2}
                                            >
                                                <div style={{display: 'flex', alignItems: 'center'}}>
                                                    <InfoOutlinedIcon style={{marginRight: 8}} />
                                                    <div>
                                                        Household income is below the minimum for age {age}.
                                                        <div style={{fontWeight: 'normal'}}>
                                                            Minimum required: {formatCurrency(requiredMinIncome)}.
                                                            Entered: {formatCurrency(hhi)}.
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                        </TableBody>
                                    )}
                                    {!tooLowHHI &&
                                        quoteValid &&
                                        data?.singleQuote?.lapseAge &&
                                        data?.singleQuote?.lapseAge < 121 && (
                                            <TableBody>
                                                <TableCell
                                                    style={{backgroundColor: '#fdecea', fontWeight: 'bold'}}
                                                    colSpan={2}
                                                >
                                                    <div style={{display: 'flex', alignItems: 'center'}}>
                                                        <InfoOutlinedIcon style={{marginRight: 8}} />
                                                        <div>
                                                            This policy will lapse prior to age 121. Please confirm with
                                                            the client.
                                                        </div>
                                                    </div>
                                                </TableCell>
                                            </TableBody>
                                        )}
                                </>
                            )}
                        </Table>
                    </TableContainer>
                ) : null}

                {displayOut
                    ?.filter((o: any) => Array.isArray(o.value))
                    .map((o: any, i: any) => {
                        const x = o.xAxis
                            ? o.xAxis
                            : new Array(o.value.length).fill(0).map((_x, innerI) => innerI + age + 1);
                        return (
                            <Grid key={'T' + i}>
                                <Plot
                                    style={{width: '100%', height: '300px'}}
                                    data={[
                                        {
                                            name: 'Count',
                                            type: 'bar',
                                            x: x,
                                            y: o.value,
                                        },
                                    ]}
                                    layout={{
                                        title: o.name,
                                        autosize: true,
                                        barmode: 'stack',
                                    }}
                                    config={{
                                        displayModeBar: false,
                                    }}
                                />
                            </Grid>
                        );
                    })}
                <AgeniumReviewDialog
                    open={isReviewDialogOpen}
                    onClose={() => setIsReviewDialogOpen(false)}
                    applicationId={ageniumCaseId}
                />
            </Grid>
        );
    },
);

QuotesTableData.displayName = 'QuotesTableData';
